import React from 'react';
import { motion } from 'framer-motion';
import { FiBookOpen, FiUsers, FiBriefcase, FiTrendingUp, FiTag, FiShoppingCart, FiZap, FiCpu } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const OtherServicesPage: React.FC = () => {
  const services = [
    {
      id: 'digital-marketing-course',
      title: 'Digital Marketing Course',
      icon: FiBookOpen,
      description: 'Comprehensive training programs to master digital marketing skills and grow your career.',
      features: [
        'SEO & SEM Fundamentals',
        'Social Media Marketing',
        'Content Marketing Strategy',
        'Email Marketing & Automation',
        'Analytics & Reporting',
        'Live Projects & Certifications'
      ],
      pricing: {
        basic: { name: 'Basic Course', price: '₹25,000', features: ['30 Days Training', '5 Modules', 'Certificate', 'Email Support'] },
        pro: { name: 'Professional', price: '₹50,000', features: ['60 Days Training', '10 Modules', 'Advanced Certification', 'Live Projects', 'Job Assistance'] },
        enterprise: { name: 'Corporate Training', price: 'Custom', features: ['Customized Curriculum', 'On-site Training', 'Unlimited Participants', 'Ongoing Support'] }
      }
    },
    {
      id: 'digital-marketing-consulting',
      title: 'Digital Marketing Consulting',
      icon: FiUsers,
      description: 'Expert consulting services to develop and execute winning digital marketing strategies.',
      features: [
        'Marketing Strategy Development',
        'Competitor Analysis',
        'Channel Mix Optimization',
        'Budget Planning & Allocation',
        'Performance Audits',
        'Growth Strategy Roadmap'
      ],
      pricing: {
        basic: { name: 'Starter Consultation', price: '₹15,000/month', features: ['Monthly Strategy Session', 'Email Support', 'Basic Audit', '2 Hour Consultation'] },
        pro: { name: 'Growth Consulting', price: '₹35,000/month', features: ['Weekly Strategy Sessions', 'Priority Support', 'Detailed Audits', '8 Hours/Month', 'Quarterly Reviews'] },
        enterprise: { name: 'Enterprise Consulting', price: 'Custom', features: ['Dedicated Consultant', '24/7 Support', 'Unlimited Sessions', 'Custom Strategy', 'Team Training'] }
      }
    },
    {
      id: 'business-consulting',
      title: 'Business Consulting',
      icon: FiBriefcase,
      description: 'Strategic business consulting to optimize operations, increase revenue, and achieve growth.',
      features: [
        'Business Model Analysis',
        'Market Research & Analysis',
        'Revenue Optimization',
        'Process Improvement',
        'Scalability Planning',
        'Digital Transformation'
      ],
      pricing: {
        basic: { name: 'Business Audit', price: '₹50,000', features: ['One-time Audit', 'Report & Recommendations', '3 Follow-up Sessions', 'Email Support'] },
        pro: { name: 'Growth Partner', price: '₹1,00,000/month', features: ['Ongoing Consulting', 'Monthly Reviews', 'Implementation Support', 'Dedicated Consultant', 'Priority Access'] },
        enterprise: { name: 'Enterprise Partnership', price: 'Custom', features: ['Full-service Consulting', 'Multiple Consultants', 'Unlimited Support', 'Custom Solutions', 'Board Presentations'] }
      }
    },
    {
      id: 'affiliate-program',
      title: 'Affiliate Program Management',
      icon: FiTrendingUp,
      description: 'Build and manage profitable affiliate programs to expand your reach and drive sales.',
      features: [
        'Affiliate Network Setup',
        'Partner Recruitment',
        'Commission Structure Design',
        'Tracking & Analytics',
        'Performance Optimization',
        'Payout Management'
      ],
      pricing: {
        basic: { name: 'Basic Setup', price: '₹30,000', features: ['Program Setup', 'Up to 50 Affiliates', 'Basic Tracking', 'Monthly Reports'] },
        pro: { name: 'Professional', price: '₹60,000/month', features: ['Up to 200 Affiliates', 'Advanced Analytics', 'Partner Recruitment', 'Weekly Reports', 'Optimization'] },
        enterprise: { name: 'Enterprise', price: 'Custom', features: ['Unlimited Affiliates', 'Dedicated Manager', 'Custom Integration', 'Real-time Analytics', 'Priority Support'] }
      }
    },
    {
      id: 'white-label',
      title: 'White Label Digital Marketing',
      icon: FiTag,
      description: 'Partner with us to offer premium digital marketing services under your brand name.',
      features: [
        'Complete White Label Solutions',
        'Your Branding Throughout',
        'Client Communication Support',
        'Detailed Reporting',
        'Scalable Services',
        'Partner Dashboard Access'
      ],
      pricing: {
        basic: { name: 'Starter Partner', price: '₹75,000/month', features: ['5 Client Projects', 'Basic Services', 'Monthly Reports', 'Email Support'] },
        pro: { name: 'Growth Partner', price: '₹1,50,000/month', features: ['15 Client Projects', 'All Services', 'Custom Reports', 'Dedicated Account Manager', 'Priority Queue'] },
        enterprise: { name: 'Agency Partner', price: 'Custom', features: ['Unlimited Projects', 'Custom Solutions', 'White Label Dashboard', '24/7 Support', 'Revenue Sharing'] }
      }
    },
    {
      id: 'ecommerce-registration',
      title: 'E-Commerce Registration & MGMT',
      icon: FiShoppingCart,
      description: 'Complete e-commerce setup, registration, and ongoing management for online marketplaces.',
      features: [
        'Amazon Seller Registration',
        'Flipkart Store Setup',
        'Product Catalog Management',
        'Inventory Synchronization',
        'Order Management',
        'Marketplace Optimization'
      ],
      pricing: {
        basic: { name: 'Basic Setup', price: '₹20,000', features: ['1 Marketplace Setup', 'Product Upload (50)', 'Basic Optimization', 'Training'] },
        pro: { name: 'Multi-Marketplace', price: '₹50,000 + ₹15K/month', features: ['3 Marketplaces', 'Product Upload (200)', 'Monthly Management', 'Inventory Sync', 'Reporting'] },
        enterprise: { name: 'Enterprise MGMT', price: 'Custom', features: ['All Marketplaces', 'Unlimited Products', 'Full Management', 'Dedicated Manager', 'Advanced Analytics'] }
      }
    },
    {
      id: 'quick-commerce',
      title: 'Quick Commerce Registration & MGMT',
      icon: FiZap,
      description: 'Get your products on quick commerce platforms like Blinkit, Zepto, and Swiggy Instamart.',
      features: [
        'Blinkit Registration',
        'Zepto Onboarding',
        'Swiggy Instamart Setup',
        'Product Listing Optimization',
        'Real-time Inventory Management',
        'Performance Analytics'
      ],
      pricing: {
        basic: { name: 'Single Platform', price: '₹25,000', features: ['1 Platform Setup', '50 Products', 'Basic Support', 'Training'] },
        pro: { name: 'Multi-Platform', price: '₹60,000 + ₹20K/month', features: ['3 Platforms', '200 Products', 'Monthly Management', 'Optimization', 'Analytics'] },
        enterprise: { name: 'Full Service', price: 'Custom', features: ['All Platforms', 'Unlimited Products', 'Complete Management', 'Dedicated Manager', '24/7 Support'] }
      }
    },
    {
      id: 'ai-consulting',
      title: 'AI Consulting',
      icon: FiCpu,
      description: 'Leverage artificial intelligence to transform your business operations and customer experience.',
      features: [
        'AI Strategy Development',
        'ChatGPT Integration',
        'AI-Powered Chatbots',
        'Machine Learning Solutions',
        'Process Automation',
        'Predictive Analytics'
      ],
      pricing: {
        basic: { name: 'AI Assessment', price: '₹40,000', features: ['AI Readiness Audit', 'Use Case Identification', 'ROI Analysis', 'Implementation Roadmap'] },
        pro: { name: 'AI Implementation', price: '₹1,50,000', features: ['Custom AI Solution', 'Integration Support', '3 Months Support', 'Training', 'Optimization'] },
        enterprise: { name: 'AI Transformation', price: 'Custom', features: ['Enterprise AI Solutions', 'Multiple Integrations', 'Ongoing Support', 'Dedicated Team', 'Custom Development'] }
      }
    }
  ];

  const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="container mx-auto px-6 relative z-10"
        >
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Specialized Digital Services
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 mb-8">
              Expand your capabilities with expert training, consulting, and cutting-edge solutions
            </p>
          </div>
        </motion.div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-50 to-transparent"></div>
      </section>

      {/* Services Sections */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="mb-32 last:mb-0"
            >
              {/* Service Header */}
              <div className="text-center mb-16">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white mb-6"
                     style={{
                       boxShadow: '0 20px 40px rgba(99, 102, 241, 0.3), inset -3px -3px 10px rgba(0, 0, 0, 0.2), inset 3px 3px 10px rgba(255, 255, 255, 0.1)'
                     }}>
                  <service.icon className="w-10 h-10" />
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-4">{service.title}</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">{service.description}</p>
              </div>

              {/* Features Grid */}
              <motion.div 
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="grid md:grid-cols-3 gap-6 mb-16"
              >
                {service.features.map((feature, idx) => (
                  <motion.div
                    key={idx}
                    variants={fadeInUp}
                    className="bg-white p-6 rounded-xl border-2 border-gray-100 hover:border-indigo-300 transition-all duration-300"
                    whileHover={{ 
                      y: -5,
                      boxShadow: '0 10px 30px rgba(99, 102, 241, 0.2)'
                    }}
                    style={{
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
                    }}
                  >
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-indigo-600 rounded-full mr-3"></div>
                      <p className="text-gray-700 font-medium">{feature}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              {/* Pricing Cards */}
              <div className="grid md:grid-cols-3 gap-8">
                {/* Basic */}
                <motion.div
                  whileHover={{ y: -10 }}
                  className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-indigo-400 transition-all duration-300"
                  style={{
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1), inset -2px -2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.pricing.basic.name}</h3>
                  <div className="text-4xl font-bold text-indigo-600 mb-6">{service.pricing.basic.price}</div>
                  <ul className="space-y-4 mb-8">
                    {service.pricing.basic.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="block w-full text-center bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-lg transition-colors duration-300"
                  >
                    Get Started
                  </Link>
                </motion.div>

                {/* Pro */}
                <motion.div
                  whileHover={{ y: -10 }}
                  className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl p-8 text-white relative transform scale-105 z-10"
                  style={{
                    boxShadow: '0 20px 50px rgba(99, 102, 241, 0.4), inset -3px -3px 10px rgba(0, 0, 0, 0.2), inset 3px 3px 10px rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <div className="absolute top-0 right-0 bg-yellow-400 text-gray-900 px-4 py-1 rounded-bl-lg rounded-tr-xl font-bold text-sm">
                    POPULAR
                  </div>
                  <h3 className="text-2xl font-bold mb-2">{service.pricing.pro.name}</h3>
                  <div className="text-4xl font-bold mb-6">{service.pricing.pro.price}</div>
                  <ul className="space-y-4 mb-8">
                    {service.pricing.pro.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="w-5 h-5 text-yellow-300 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="block w-full text-center bg-white text-indigo-600 hover:bg-gray-100 font-semibold py-3 rounded-lg transition-colors duration-300"
                  >
                    Get Started
                  </Link>
                </motion.div>

                {/* Enterprise */}
                <motion.div
                  whileHover={{ y: -10 }}
                  className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-purple-400 transition-all duration-300"
                  style={{
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1), inset -2px -2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.pricing.enterprise.name}</h3>
                  <div className="text-4xl font-bold text-purple-600 mb-6">{service.pricing.enterprise.price}</div>
                  <ul className="space-y-4 mb-8">
                    {service.pricing.enterprise.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="block w-full text-center bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-lg transition-colors duration-300"
                  >
                    Contact Sales
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-gray-100 mb-10 max-w-3xl mx-auto">
              Let's discuss how our specialized services can help you achieve your goals
            </p>
            <Link
              to="/contact"
              className="inline-block bg-white text-indigo-600 px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105"
              style={{
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
              }}
            >
              Get Free Consultation
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OtherServicesPage;
